/**
 * Team Site Visits Panel — read-only CEO/HOD view of agent locations and
 * active site visits across the team. Polls /api/site-visits/team every 30s.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import HelpInfo from './HelpInfo';
import { Navigation, Clock, MapPin, RefreshCw, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 30 * 1000;

const formatDuration = (seconds) => {
  if (seconds == null || seconds < 0) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

const TeamSiteVisitsPanel = () => {
  const { token } = useAuth();
  const [visits, setVisits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tickNow, setTickNow] = useState(Date.now());
  const [refreshedAt, setRefreshedAt] = useState(null);

  // 1-second tick for live timers.
  useEffect(() => {
    const t = setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchTeam = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/site-visits/team?token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (data.success) {
        setVisits(data.visits || []);
        setLocations(data.locations || []);
        setRefreshedAt(new Date());
      }
    } catch (e) {
      console.warn('[TEAM_GPS] fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTeam();
    const t = setInterval(fetchTeam, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchTeam]);

  const activeVisits = visits.filter((v) => !v.departure_time);
  const completedVisits = visits.filter((v) => v.departure_time);

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Navigation className="w-4 h-4 text-emerald-600" />
          Team Site Visits & Live Locations
          <HelpInfo
            align="inline"
            title="How to read Team Site Visits"
            steps={[
              'Two lists side-by-side: agents Currently on site (active visits with no departure time yet) and Recent visits (closed today).',
              <>Each card shows the agent, the property they're at, when they arrived, and how long they've been there. GPS-verified visits get a green checkmark.</>,
              <>Click <em>Refresh</em> any time, but the panel also polls automatically every 30 seconds so you don't have to keep clicking.</>,
              'Long open visits with no progress note (e.g. 4h+ silent) usually mean the agent forgot to close out — worth a quick ping.',
            ]}
            tip="Pair this with the live map above. If an active visit doesn't have a matching pin, the agent's GPS is probably off — coach them to enable it."
          />
          <span className="ml-auto flex items-center gap-2">
            {refreshedAt && (
              <span className="text-xs font-normal text-slate-500">
                Updated {refreshedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchTeam} title="Refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Active visits */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Currently on site ({activeVisits.length})
          </h4>
          {activeVisits.length === 0 ? (
            <p className="text-sm text-slate-500">No agents currently inside a property's geofence.</p>
          ) : (
            <div className="space-y-2">
              {activeVisits.map((v) => {
                const arrivedMs = v.arrival_time ? new Date(v.arrival_time).getTime() : null;
                const elapsed = arrivedMs ? Math.max(0, Math.floor((tickNow - arrivedMs) / 1000)) : 0;
                return (
                  <div key={v.id} className="flex items-center justify-between p-3 rounded-lg border border-emerald-200 bg-emerald-50">
                    <div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 size={14} className="text-emerald-600" />
                        <span className="font-medium text-emerald-900">{v.agent_name || 'Agent'}</span>
                        <span className="text-emerald-700 text-sm">at {v.property_name}</span>
                        {v.auto_detected && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 border text-xs">
                            Auto
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-emerald-800 mt-0.5 font-mono">
                        <Clock size={11} className="inline mr-1" />
                        {formatDuration(elapsed)} on site · arrived {arrivedMs ? new Date(arrivedMs).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Live locations (most-recent ping per agent) */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Live agent locations ({locations.length})
          </h4>
          {locations.length === 0 ? (
            <p className="text-sm text-slate-500">No live pings in the last 30 minutes.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {locations.map((loc) => (
                <div key={loc.agent_id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{loc.agent_name || loc.agent_id}</p>
                    <p className="text-xs text-slate-500 font-mono">
                      <MapPin size={11} className="inline mr-1" />
                      {Number(loc.lat).toFixed(5)}, {Number(loc.lng).toFixed(5)}
                    </p>
                  </div>
                  <span className="text-xs text-slate-400 ml-2 flex-shrink-0">
                    {loc.updated_at ? new Date(loc.updated_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent completed visits */}
        {completedVisits.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Completed today ({completedVisits.length})
            </h4>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {completedVisits.slice(0, 20).map((v) => (
                <div key={v.id} className="flex items-center justify-between p-2 rounded text-xs border border-slate-100">
                  <div className="min-w-0">
                    <span className="font-medium text-slate-700">{v.agent_name || 'Agent'}</span>
                    <span className="text-slate-500"> · {v.property_name}</span>
                  </div>
                  <span className="text-slate-500 font-mono">
                    {formatDuration(v.duration_seconds || 0)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamSiteVisitsPanel;
